import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaTimes, FaTicketAlt, FaPaperPlane, FaExclamationCircle, 
  FaSpinner
} from 'react-icons/fa';
import styles from '../CSS/CreateTicketModal.module.css';

const CreateTicketModal = ({ user, departamentos, adminsDepartamento, onClose, onCreate }) => {
  // Estado del formulario
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion: '',
    departamento_id: departamentos.length > 0 ? departamentos[0].id : ''
  });
  
  // Estado de errores
  const [errors, setErrors] = useState({});
  
  // Estado de carga cuando se está enviando el formulario
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Límites de caracteres
  const TITULO_MAX_LENGTH = 100;
  const DESCRIPCION_MAX_LENGTH = 350;
  
  // Función para enviar notificaciones de nuevo ticket
  const enviarNotificacionNuevoTicket = async (ticket, administradores) => {
  try {
    // CAMBIO: Se quita la URL completa y se deja la ruta relativa
    const response = await fetch('https://truebavirtual.onrender.com/api/new-ticket/notify-new-ticket', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ticket: {
          ticket_id: ticket.ticket_id || `#${ticket.id.slice(0, 8)}`,
          titulo: ticket.titulo,
          descripcion: ticket.descripcion,
          departamento_nombre: ticket.departamento_nombre,
          created_by_name: ticket.created_by_name,
          created_by_email: ticket.created_by_email,
          id: ticket.id
        },
        adminsDepartamento: administradores
      })
    });

    const result = await response.json();
    return result;

  } catch (error) {
    // AÑADIDO: Un console.error para ver el fallo en la consola del navegador
    console.error('Error de conexión enviando notificación a admins:', error);
    return {
      success: false,
      message: 'Error de conexión con el servidor de notificaciones',
      emailsSent: 0
    };
  }
};
  
  // Manejador de cambios en los campos
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Aplicar límites de caracteres
    let finalValue = value;
    if (name === 'titulo' && value.length > TITULO_MAX_LENGTH) {
      finalValue = value.substring(0, TITULO_MAX_LENGTH);
    }
    if (name === 'descripcion' && value.length > DESCRIPCION_MAX_LENGTH) {
      finalValue = value.substring(0, DESCRIPCION_MAX_LENGTH);
    }
    
    setFormData(prevData => ({
      ...prevData,
      [name]: finalValue
    }));
    
    // Limpiar error del campo si existe
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: ''
      }));
    }
  };
  
  // Validación del formulario
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.titulo.trim()) {
      newErrors.titulo = 'El título es obligatorio';
    } else if (formData.titulo.length < 5) {
      newErrors.titulo = 'El título debe tener al menos 5 caracteres';
    }
    
    if (!formData.descripcion.trim()) {
      newErrors.descripcion = 'La descripción es obligatoria';
    } else if (formData.descripcion.length < 10) {
      newErrors.descripcion = 'La descripción debe tener al menos 10 caracteres';
    }
    
    if (!formData.departamento_id) {
      newErrors.departamento_id = 'Debes seleccionar un departamento';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Función para enviar confirmación al usuario que creó el ticket
const enviarConfirmacionUsuario = async (ticket, userEmail, userName) => {
  try {
    // CAMBIO: Se quita la URL completa y se deja la ruta relativa
    const response = await fetch('https://truebavirtual.onrender.com/api/new-ticket/send-confirmation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ticket: {
          ticket_id: ticket.ticket_id || `#${ticket.id.slice(0, 8)}`,
          titulo: ticket.titulo,
          descripcion: ticket.descripcion,
          departamento_nombre: ticket.departamento_nombre,
          id: ticket.id
        },
        userEmail: userEmail,
        userName: userName
      })
    });

    const result = await response.json();
    return result;
    
  } catch (error) {
    console.error('Error enviando confirmación al usuario:', error);
    return {
      success: false,
      message: 'Error de conexión con el servidor de confirmaciones'
    };
  }
};

// Envío del formulario con notificaciones automáticas Y confirmación al usuario
const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (validateForm()) {
    setIsSubmitting(true);
    
    try {
      // Preparar datos del ticket con información completa del usuario
      const ticketData = {
        ...formData,
        departamento_nombre: departamentos.find(d => d.id === formData.departamento_id)?.nombre || 'Desconocido',
        created_by_email: user.email || '',
        created_by_name: user.displayName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Usuario'
      };
      
      // Añadir campos adicionales si existen
      if (user.displayName) {
        ticketData.created_by_display_name = user.displayName;
      }
      if (user.firstName) {
        ticketData.created_by_first_name = user.firstName;
      }
      if (user.lastName) {
        ticketData.created_by_last_name = user.lastName;
      }
      
      // 1. Crear el ticket en la base de datos
      const ticketCreado = await onCreate(ticketData);
      
      if (ticketCreado && ticketCreado.success) {
        // 2. Preparar datos completos del ticket para notificaciones
        const ticketCompleto = {
          ...ticketData,
          id: ticketCreado.ticketId || ticketCreado.id,
          ticket_id: ticketCreado.ticket_id || `#TK${Date.now().toString().slice(-6)}`
        };
        
        // 3. Identificar administradores del departamento
        const adminsDelDepartamento = adminsDepartamento ? 
          adminsDepartamento.filter(admin => admin.departamento_id === formData.departamento_id) : 
          [];
        
        // 4. Preparar nombre del usuario para los emails
        const nombreUsuario = user.displayName || 
                             user.name || 
                             `${user.firstName || ''} ${user.lastName || ''}`.trim() || 
                             'Usuario';
        
        // 5. Enviar emails en paralelo (no bloqueante)
        const emailPromises = [];
        
        // Enviar notificaciones a administradores si hay
        if (adminsDelDepartamento.length > 0) {
          emailPromises.push(
            enviarNotificacionNuevoTicket(ticketCompleto, adminsDelDepartamento)
              .then(result => ({
                type: 'admin_notifications',
                success: result.success,
                emailsSent: result.emailsSent || 0,
                message: result.message
              }))
              .catch(error => ({
                type: 'admin_notifications',
                success: false,
                emailsSent: 0,
                message: 'Error enviando notificaciones a administradores'
              }))
          );
        }
        
        // Enviar confirmación al usuario si tiene email
        if (user.email) {
          emailPromises.push(
            enviarConfirmacionUsuario(ticketCompleto, user.email, nombreUsuario)
              .then(result => ({
                type: 'user_confirmation',
                success: result.success,
                message: result.message
              }))
              .catch(error => ({
                type: 'user_confirmation',
                success: false,
                message: 'Error enviando confirmación al usuario'
              }))
          );
        }
        
        // 6. Ejecutar envío de emails (sin esperar a que terminen)
        if (emailPromises.length > 0) {
          Promise.all(emailPromises)
            .then(results => {
              console.log('📧 Resultados de envío de emails:', results);
              
              // Log de resultados para debugging
              results.forEach(result => {
                if (result.success) {
                  console.log(`✅ ${result.type}: ${result.message}`);
                } else {
                  console.warn(`⚠️ ${result.type}: ${result.message}`);
                }
              });
            })
            .catch(error => {
              console.error('❌ Error general en envío de emails:', error);
            });
        }
        
        // 7. Cerrar modal inmediatamente (no esperamos por los emails)
        onClose();
        
      } else {
        throw new Error('Error al crear el ticket en la base de datos');
      }
      
    } catch (error) {
      setErrors({ 
        submit: error.message || 'Error al crear el ticket. Inténtalo de nuevo.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  }
};
  
  // Animaciones para el modal
  const modalVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };
  
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } }
  };
  
  return (
    <motion.div 
      className={styles.modalOverlay}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={overlayVariants}
    >
      <motion.div 
        className={styles.modalContent}
        variants={modalVariants}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <FaTicketAlt />
            <span>Crear Nuevo Ticket</span>
          </h2>
          <button 
            className={styles.closeButton} 
            onClick={onClose} 
            aria-label="Cerrar"
            disabled={isSubmitting}
          >
            <FaTimes />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className={styles.modalBody}>
            {errors.submit && (
              <div className={styles.errorMessage}>
                <FaExclamationCircle />
                {errors.submit}
              </div>
            )}
            
            <div className={styles.formGroup}>
              <label htmlFor="titulo" className={styles.formLabel}>
                Título del Ticket*
              </label>
              <input
                type="text"
                id="titulo"
                name="titulo"
                className={styles.formControl}
                value={formData.titulo}
                onChange={handleChange}
                placeholder="Escribe un título descriptivo"
                disabled={isSubmitting}
                autoFocus
                maxLength={TITULO_MAX_LENGTH}
              />
              <div className={styles.characterCount}>
                {formData.titulo.length}/{TITULO_MAX_LENGTH} caracteres
              </div>
              {errors.titulo && (
                <div className={styles.fieldError}>
                  <FaExclamationCircle />
                  {errors.titulo}
                </div>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="descripcion" className={styles.formLabel}>
                Descripción*
              </label>
              <textarea
                id="descripcion"
                name="descripcion"
                className={styles.formControl}
                value={formData.descripcion}
                onChange={handleChange}
                placeholder="Describe detalladamente tu problema o solicitud"
                rows={5}
                disabled={isSubmitting}
                maxLength={DESCRIPCION_MAX_LENGTH}
              />
              <div className={styles.characterCount}>
                {formData.descripcion.length}/{DESCRIPCION_MAX_LENGTH} caracteres
              </div>
              {errors.descripcion && (
                <div className={styles.fieldError}>
                  <FaExclamationCircle />
                  {errors.descripcion}
                </div>
              )}
            </div>
            
            <div className={styles.formGroup}>
              <label htmlFor="departamento_id" className={styles.formLabel}>
                Departamento*
              </label>
              <select
                id="departamento_id"
                name="departamento_id"
                className={styles.formControl}
                value={formData.departamento_id}
                onChange={handleChange}
                disabled={isSubmitting || departamentos.length === 0}
              >
                {departamentos.length === 0 ? (
                  <option value="">No hay departamentos disponibles</option>
                ) : (
                  departamentos.map(departamento => (
                    <option key={departamento.id} value={departamento.id}>
                      {departamento.nombre}
                    </option>
                  ))
                )}
              </select>
              {errors.departamento_id && (
                <div className={styles.fieldError}>
                  <FaExclamationCircle />
                  {errors.departamento_id}
                </div>
              )}
            </div>
          </div>
          
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={`${styles.btn} ${styles.btnSecondary}`}
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancelar
            </button>
            
            <button
              type="submit"
              className={`${styles.btn} ${styles.btnPrimary}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className={styles.spinner} />
                  Creando...
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  Crear Ticket
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default CreateTicketModal;